import React, { useState } from 'react';
import styled from 'styled-components';
import { Mail, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { supabase } from '../../utils/supabase';
import { isAdminEmail } from '../../utils/adminWhitelist';

const LoginContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f8fb 0%, #ffffff 100%);
  padding: 2rem;
`;

const LoginCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 3rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  max-width: 450px;
  width: 100%;
`;

const Title = styled.h1`
  font-size: 2rem;
  color: var(--secondary-color);
  margin-bottom: 0.5rem;
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: 1rem;
  color: var(--gray-600);
  text-align: center;
  margin-bottom: 2rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--secondary-color);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.875rem 1rem 0.875rem 3rem;
  border: 2px solid var(--gray-200);
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(255, 143, 0, 0.1);
  }

  &:disabled {
    background-color: var(--gray-100);
    cursor: not-allowed;
  }
`;

const InputIcon = styled.div`
  position: absolute;
  left: 1rem;
  color: var(--gray-500);
  display: flex;
  align-items: center;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem;
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-dark) 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(255, 143, 0, 0.3);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(255, 143, 0, 0.4);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const Message = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  border-radius: 8px;
  font-size: 0.9375rem;
  background-color: ${props => props.$type === 'success' ? 'var(--success-light)' : 'var(--danger-light)'};
  color: ${props => props.$type === 'success' ? 'var(--success-color)' : 'var(--danger-color)'};
  border: 1px solid ${props => props.$type === 'success' ? 'var(--success-color)' : 'var(--danger-color)'};
`;

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Clear previous messages
    setMessage(null);

    // Validate email format
    if (!email || !email.includes('@')) {
      setMessage({ type: 'error', text: 'Please enter a valid email address.' });
      return;
    }

    // Check if email is in admin whitelist
    if (!isAdminEmail(email)) {
      setMessage({
        type: 'error',
        text: 'This email is not authorized for admin access.'
      });
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: email.trim(),
        options: {
          emailRedirectTo: `${window.location.origin}`,
        }
      });

      if (error) throw error;

      setMessage({
        type: 'success',
        text: 'Magic link sent! Check your email and click the link to log in.'
      });

      // Clear email field
      setEmail('');
    } catch (error) {
      console.error('Error sending magic link:', error);
      setMessage({
        type: 'error',
        text: error.message || 'Failed to send magic link. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginContainer>
      <LoginCard>
        <Title>Admin Login</Title>
        <Subtitle>Enter your email to receive a magic link</Subtitle>

        <Form onSubmit={handleSubmit}>
          <InputGroup>
            <Label htmlFor="email">Email Address</Label>
            <InputWrapper>
              <InputIcon>
                <Mail size={20} />
              </InputIcon>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your.email@indus-bulklogistics.com"
                disabled={loading}
                required
              />
            </InputWrapper>
          </InputGroup>

          {message && (
            <Message $type={message.type}>
              {message.type === 'success' ? (
                <CheckCircle size={20} />
              ) : (
                <AlertCircle size={20} />
              )}
              <span>{message.text}</span>
            </Message>
          )}

          <Button type="submit" disabled={loading}>
            <Send size={20} />
            {loading ? 'Sending...' : 'Send Magic Link'}
          </Button>
        </Form>
      </LoginCard>
    </LoginContainer>
  );
};

export default AdminLogin;
